<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import {
  ChevronDown, User, CircleHelp, LogOut, Settings, Moon, Sun,
  CreditCard, UserRoundCog, ShieldCheck, ChevronRight, X, Menu,
} from 'lucide-vue-next'
import { Button } from '@/Components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu'
import { useDarkMode } from '@/composables/useDarkMode'
import BrandLogo from '@/Components/BrandLogo.vue'
import {
  home, becomeATutor, login, logout, tutors, subjects, parents, faq, howItWorks,
} from '@/routes/index'
import teacher from '@/routes/teacher'

/**
 * One header for the whole product.
 *
 * The dashboards used to carry their own sidebar with their own logo, their own
 * account menu and their own help link, so signing in changed the furniture as
 * well as the content. There is now a single bar: the marketing pages and the
 * dashboards share it, and what changes is which links it holds.
 *
 * Signed out — the six marketing pages. Pricing is not among them: it argued for
 * a price list that no longer exists, and the tutors' own prices are on the
 * listing. Become a tutor takes its place, because recruiting tutors is the
 * other half of what the public site is for and it had no entry at all.
 * Signed in — the working links for that role first, then the public pages a
 * member still uses. How it works, For parents and Become a tutor come out
 * entirely: they sell the product to someone who has already bought it. They
 * stay in the footer.
 */
const emit = defineEmits(['open-support', 'open-impersonate'])

const page = usePage()
const user = computed(() => page.props.auth?.user)
const isTeacher = computed(() => user.value?.user_type === 'teacher')
const isStudent = computed(() => user.value?.user_type === 'student')
const isAdmin = computed(() => ['system', 'limited-system', 'system-viewer'].includes(user.value?.user_type))
const impersonateEnabled = computed(() => page.props.impersonate?.enabled)
const unreadMessageCount = computed(() => page.props.unreadMessageCount ?? 0)


const isMobileOpen = ref(false)
const { isDark, toggleDarkMode } = useDarkMode()

const guestNavigation = computed(() => [
  { name: 'How it works', href: howItWorks(), match: '/how-it-works' },
  { name: 'Our tutors', href: tutors(), match: '/tutors' },
  { name: 'Subjects', href: subjects(), match: '/subjects' },
  { name: 'For parents', href: parents(), match: '/for-parents' },
  { name: 'Become a tutor', href: becomeATutor(), match: '/become-a-tutor' },
  { name: 'FAQ', href: faq(), match: '/faq' },
])

/**
 * "Explore" is deliberately absent. It was the student's own tutor listing —
 * the same thing as Our tutors, reached by a different name, which is how a
 * product ends up with two front doors to one room.
 */
const studentNavigation = computed(() => [
  { name: 'Dashboard', href: '/student/dashboard', match: '/student/dashboard' },
  { name: 'My resources', href: '/student/resources', match: '/student/resources' },
  { name: 'Schedule', href: '/student/schedule', match: '/student/schedule' },
  { name: 'Messages', href: '/student/messages', match: '/student/messages', badge: unreadMessageCount.value },
  { name: 'Subscriptions', href: '/student/subscriptions', match: '/student/subscriptions' },
])

const teacherNavigation = computed(() => [
  { name: 'Dashboard', href: '/teacher/dashboard', match: '/teacher/dashboard' },
  { name: 'My content', href: teacher.content.index.url(), match: '/teacher/content' },
  { name: 'My students', href: teacher.students.index.url(), match: '/teacher/students' },
  { name: 'Messages', href: '/teacher/messages', match: '/teacher/messages', badge: unreadMessageCount.value },
  { name: 'Earnings', href: '/teacher/earnings', match: '/teacher/earnings' },
  { name: 'Calendar', href: '/teacher/schedules', match: '/teacher/schedules' },
])

/**
 * The public links a signed-in person still has a use for. They sit after the
 * role's own links: someone who is signed in came to do something, and their
 * own section is what they came for.
 *
 * A tutor does not get "Our tutors" — it is the shop window for the service they
 * provide, and their own listing is under My students.
 */
const publicWhenSignedIn = computed(() => [
  ...(isTeacher.value ? [] : [{ name: 'Our tutors', href: tutors(), match: '/tutors' }]),
  { name: 'Subjects', href: subjects(), match: '/subjects' },
])

/**
 * Administrators get the public site plus a way into the panel, in the same
 * position a student or a tutor finds their own dashboard — the panel is their
 * dashboard.
 */
const adminNavigation = computed(() => [
  { name: 'Dashboard', href: '/admin', match: '/admin', external: true },
  ...guestNavigation.value,
])

const navigation = computed(() => {
  if (!user.value) return guestNavigation.value
  if (isTeacher.value) return [...teacherNavigation.value, ...publicWhenSignedIn.value]
  if (isStudent.value) return [...studentNavigation.value, ...publicWhenSignedIn.value]
  return adminNavigation.value
})

/**
 * Six guest links fit from `lg`; a signed-in bar carries eight or nine and needs
 * `xl` before it stops wrapping. Pinning both at `xl` made the guest menu vanish
 * on a 1024–1279 window, which is a common laptop.
 */
const navBreakpoint = computed(() => (isStudent.value || isTeacher.value ? 'xl' : 'lg'))

const isCurrent = (item) => item.match.startsWith('/') && page.url.startsWith(item.match)

const closeDrawer = () => { isMobileOpen.value = false }
const onKeydown = (event) => { if (event.key === 'Escape') closeDrawer() }

watch(isMobileOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  open
    ? document.addEventListener('keydown', onKeydown)
    : document.removeEventListener('keydown', onKeydown)
})

watch(() => page.url, closeDrawer)

onBeforeUnmount(() => {
  document.body.style.overflow = ''
  document.removeEventListener('keydown', onKeydown)
})

const initials = (name) => (name || '').split(' ').map((n) => n.charAt(0)).join('').toUpperCase().substring(0, 2)
</script>

<template>
  <header class="sticky top-0 z-50 border-b border-line bg-surface/85 backdrop-blur-md backdrop-saturate-150">
    <div class="container-page flex h-16 items-center gap-5 md:h-18">
      <Link :href="home()" class="shrink-0" aria-label="Bindle — home">
        <BrandLogo />
      </Link>

      <nav class="hidden items-center gap-0.5" :class="navBreakpoint === 'xl' ? 'xl:flex' : 'lg:flex'">
        <component
          :is="item.external ? 'a' : Link"
          v-for="item in navigation"
          :key="item.name"
          :href="item.href"
          :class="[
            'relative rounded-md px-2.5 py-2 text-sm font-semibold transition-colors duration-150',
            'after:absolute after:inset-x-2.5 after:-bottom-0.5 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:bg-brand after:transition-transform after:duration-200 after:ease-brand hover:after:scale-x-100',
            isCurrent(item) ? 'text-brand after:scale-x-100' : 'text-ink hover:text-brand',
          ]"
          :aria-current="isCurrent(item) ? 'page' : undefined"
        >
          {{ item.name }}
          <span
            v-if="item.badge"
            class="ml-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white"
          >{{ item.badge }}</span>
        </component>
      </nav>

      <div class="ml-auto flex items-center gap-1 sm:gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          @click="toggleDarkMode"
        >
          <Sun v-if="isDark" class="size-[18px]" />
          <Moon v-else class="size-[18px]" />
        </Button>

        <template v-if="user">
          <!--
            The notification bell has gone. It rendered a permanent red dot and
            opened nothing — there is no notification centre behind it — so it
            promised unread news on every page, forever.
          -->
          <!--
            No Dashboard button. Every role now has a Dashboard entry in the
            navigation itself — students and tutors to their own section,
            administrators to the panel — so a second control beside the avatar
            was the same destination twice.
          -->
          <!--
            Avatar and chevron in one bordered pill.

            The pill is drawn on the inner span, not on the trigger, and the
            trigger is the menu's own button rather than one of ours passed in
            through `as-child`. That is deliberate: `as-child` makes the menu
            clone our element and merge its own attributes onto it, and when the
            menu opened it re-applied that merge and our classes lost — the pill
            vanished, the button stopped being a flex row, and the chevron
            dropped onto a second line under the avatar. Nothing the menu does to
            the button can reach the span inside it.
          -->
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="Account menu"
              class="ml-1 rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <span class="inline-flex items-center gap-0.5 rounded-full border border-line bg-surface p-0.5 pr-1.5 transition-colors hover:bg-surface-2">
                <Avatar class="size-8 shrink-0">
                  <AvatarImage v-if="user.avatar_url" :src="user.avatar_url" :alt="user.name" />
                  <AvatarFallback class="text-xs">{{ initials(user.name) }}</AvatarFallback>
                </Avatar>
                <ChevronDown class="size-4 shrink-0 text-muted" />
              </span>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" class="w-56">
              <!--
                No Dashboard entry here either. It was a small-screen fallback
                from when the bar had no Dashboard link; the bar now has one for
                every role, and the drawer repeats the whole bar, so this was the
                same destination a third time.
              -->
              <template v-if="isTeacher">
                <DropdownMenuItem as-child>
                  <Link :href="teacher.settings.personalInfo()" class="flex w-full cursor-pointer items-center">
                    <User class="mr-2 size-4" /><span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem as-child>
                  <Link :href="teacher.settings.index.url()" class="flex w-full cursor-pointer items-center">
                    <Settings class="mr-2 size-4" /><span>Settings</span>
                  </Link>
                </DropdownMenuItem>
              </template>

              <template v-if="isStudent">
                <DropdownMenuItem as-child>
                  <Link href="/student/settings/profile" class="flex w-full cursor-pointer items-center">
                    <User class="mr-2 size-4" /><span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem as-child>
                  <Link href="/student/subscriptions" class="flex w-full cursor-pointer items-center">
                    <CreditCard class="mr-2 size-4" /><span>Subscriptions</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem as-child>
                  <Link href="/student/settings/notifications" class="flex w-full cursor-pointer items-center">
                    <Settings class="mr-2 size-4" /><span>Settings</span>
                  </Link>
                </DropdownMenuItem>
              </template>

              <template v-if="isAdmin">
                <DropdownMenuSeparator />
                <DropdownMenuItem as-child>
                  <a href="/admin" class="flex w-full cursor-pointer items-center text-brand">
                    <ShieldCheck class="mr-2 size-4" /><span>Admin panel</span>
                  </a>
                </DropdownMenuItem>
              </template>

              <template v-if="impersonateEnabled">
                <DropdownMenuSeparator />
                <!-- Its own event. It briefly shared `open-support`, so choosing
                     Impersonate opened the help dialog instead. -->
                <DropdownMenuItem class="flex w-full cursor-pointer items-center text-warning" @click="emit('open-impersonate')">
                  <UserRoundCog class="mr-2 size-4" /><span>Impersonate</span>
                </DropdownMenuItem>
              </template>

              <DropdownMenuSeparator />
              <DropdownMenuItem class="flex w-full cursor-pointer items-center" @click="emit('open-support')">
                <CircleHelp class="mr-2 size-4" /><span>Help &amp; support</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuItem as-child>
                <Link :href="logout()" method="post" as="button" class="flex w-full cursor-pointer items-center text-danger">
                  <LogOut class="mr-2 size-4" /><span>Sign out</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </template>

        <template v-else>
          <Link :href="login()" class="hidden lg:block">
            <Button variant="ghost" size="sm">Log in</Button>
          </Link>
          <Link :href="tutors()" class="hidden sm:block">
            <Button size="sm">Get started</Button>
          </Link>
        </template>

        <Button
          variant="ghost"
          size="icon-sm"
          :class="navBreakpoint === 'xl' ? 'xl:hidden' : 'lg:hidden'"
          aria-label="Open menu"
          :aria-expanded="isMobileOpen"
          @click="isMobileOpen = true"
        >
          <Menu class="size-5" />
        </Button>
      </div>
    </div>
  </header>

  <!-- ==================== DRAWER ==================== -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0" leave-to-class="opacity-0"
    >
      <div v-if="isMobileOpen" class="fixed inset-0 z-90 bg-ink/45"
        :class="navBreakpoint === 'xl' ? 'xl:hidden' : 'lg:hidden'" @click="closeDrawer"></div>
    </Transition>

    <Transition
      enter-active-class="transition-transform duration-300 ease-out"
      leave-active-class="transition-transform duration-200 ease-in"
      enter-from-class="translate-x-full" leave-to-class="translate-x-full"
    >
      <aside
        v-if="isMobileOpen"
        class="fixed inset-y-0 right-0 z-100 flex w-[min(20rem,88vw)] flex-col bg-surface shadow-lg"
        :class="navBreakpoint === 'xl' ? 'xl:hidden' : 'lg:hidden'"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
        <div class="flex h-16 items-center justify-between border-b border-line px-5">
          <BrandLogo :compact="true" />
          <Button variant="ghost" size="icon-sm" aria-label="Close menu" @click="closeDrawer">
            <X class="size-5" />
          </Button>
        </div>

        <nav class="flex-1 overflow-y-auto p-3">
          <component
            :is="item.external ? 'a' : Link"
            v-for="item in navigation"
            :key="item.name"
            :href="item.href"
            :class="[
              'flex items-center justify-between rounded-md px-3 py-3 text-[15px] font-semibold transition-colors',
              isCurrent(item) ? 'bg-brand-soft text-brand-soft-text' : 'text-ink hover:bg-surface-2',
            ]"
          >
            <span class="flex items-center gap-2">
              {{ item.name }}
              <span
                v-if="item.badge"
                class="inline-flex min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white"
              >{{ item.badge }}</span>
            </span>
            <ChevronRight class="size-4 opacity-50" />
          </component>

          <div class="my-3 border-t border-line"></div>

          <Link v-if="!user" :href="login()" class="flex items-center justify-between rounded-md px-3 py-3 text-[15px] font-semibold text-ink transition-colors hover:bg-surface-2">
            Log in <ChevronRight class="size-4 opacity-50" />
          </Link>
        </nav>

        <div v-if="!user" class="border-t border-line p-4">
          <Link :href="tutors()" class="block">
            <Button class="w-full" size="lg">Get started</Button>
          </Link>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>
